import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CohortUserStore } from '@stores';
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useApp } from '@app/App.hook';
import { useAppCookies } from '@app/providers/AppCookieProvider';

interface ActiveCohortUserGuardProps {
  children: React.ReactNode;
}

export const ActiveCohortUserGuard: React.FC<ActiveCohortUserGuardProps> = ({ children }) => {
  const { groupId } = useParams<{ groupId: string }>();
  const cookies = useAppCookies();
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState<boolean>(false);
  const { logout } = useApp();

  useEffect(() => {
    // If cookies are missing, we already know the answer: no active cohort
    if (!cookies.profile.available || !groupId || !cookies.auth.available) {
      setIsActive(false);
      setLoading(false);
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const store = new CohortUserStore();
        const { result } = await store.readByUserAndCohortId(cookies.profile.id, Number(groupId));
        if (isMounted) setIsActive(result.isActive);
      } catch {
        if (isMounted) setIsActive(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [cookies.profile?.id, cookies.auth?.available, groupId]);

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!isActive) {
    return (
      <Dialog
        open
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
        }}
      >
        <DialogTitle>Something&#39;s Not Quite Right</DialogTitle>
        <DialogContent>
          <Typography>
            🤔 We don&#39;t have you rostered with this We Hate Exercise team. If this is incorrect, please reach out to
            your team captain.
          </Typography>
        </DialogContent>
        <Button variant={'contained'} onClick={logout} sx={{ margin: '0 auto 25px', width: '80%' }}>
          Logout
        </Button>
      </Dialog>
    );
  }

  return <>{children}</>;
};
